"use client";

import UnicornLogo from "@/assets/logos/unicorn-tracker-logo.png";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  CaretDown,
  Faders,
  X,
} from "@phosphor-icons/react/dist/ssr";
import Image from "next/image";
import Link from "next/link";
import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import UnicornFilters from "./filters";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import {
  getUnicornRegions,
  getCountries,
  getUnicornRegionCountries,
} from "@/services/api/UnicornTracker";

const UnicornTopbar = memo(function UnicornTopbar({ filters, setFilters }) {
  const [openFilters, setOpenFilters] = useState(false);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCountryFilter, setOpenCountryFilter] = useState(false);
  const [searchCountryQuery, setSearchCountryQuery] = useState("");

  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // current values from URL
  const selectedUnicornRegionId = searchParams.get("unicorn_regions");
  const selectedCountryId = searchParams.get("country");

  // Memoize the fetch functions
  const fetchMasterData = useCallback(async () => {
    try {
      const [regionsRes, countriesRes] = await Promise.all([
        getUnicornRegions({ params: { dropdown: true, has_unicorn: true } }),
        getCountries({
          params: { dropdown: true, searchStartupDropdown: true },
        }),
      ]);
      const regionsData = regionsRes.data || [];
      const countriesData = countriesRes.data || [];

      setRegions(regionsData);
      setAllCountries(countriesData);
      setCountries(countriesData);
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  // Memoize the country filtering function
  const filterCountriesByUnicornRegion = useCallback(async () => {
    if (selectedUnicornRegionId && regions.length > 0) {
      try {
        const countriesRes = await getUnicornRegionCountries(
          selectedUnicornRegionId,
          {
            params: { dropdown: true, searchStartupDropdown: true },
          },
        );
        setCountries(countriesRes.data || []);
      } catch (error) {
        console.error("Error fetching countries by unicorn region:", error);
        // Fallback to all countries if region filtering fails
        setCountries(allCountries);
      }
    } else {
      // No region selected, show all countries
      setCountries(allCountries);
    }
  }, [selectedUnicornRegionId, regions, allCountries]);

  // Filter countries when unicorn region changes
  useEffect(() => {
    filterCountriesByUnicornRegion();
  }, [filterCountriesByUnicornRegion]);

  const updateQuery = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams.toString());

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }

      // If updating unicorn region, clear the country selection since it may not be valid for new region
      if (key === "unicorn_regions") {
        params.delete("country");
      }

      router.push(`${pathname}?${params.toString()}`);
    },
    [searchParams, pathname, router],
  );

  // Memoize helper functions to get display names from IDs
  const getUnicornRegionName = useCallback(
    (unicornRegionId) => {
      const region = regions.find((r) => r.id.toString() === unicornRegionId);
      return region ? region.name : "Select Unicorn Region";
    },
    [regions],
  );

  const getCountryName = useCallback(
    (countryId) => {
      if (!countryId) return "Select Country";

      const country = countries.find((c) => {
        const cid = c.country_id ?? c.id;
        return cid && cid.toString() === countryId.toString();
      });
      return country ? country.country_name || country.name : "Select Country";
    },
    [countries],
  );

  const getCountryId = useCallback((country) => {
    const cid = country.country_id ?? country.id;
    return cid?.toString();
  }, []);

  // Check if any advanced filters are active based on URL parameters
  const hasActiveAdvancedFilters = useMemo(() => {
    const sectors = searchParams.get("sectors");
    const cities = searchParams.get("cities");
    const fundingStages = searchParams.get("funding_stages");
    const valuationCondition = searchParams.get("valuation_condition");
    const valuationAmount = searchParams.get("valuation_amount");
    const yearFoundedCondition = searchParams.get("year_founded_condition");
    const yearFoundedYear = searchParams.get("year_founded_year");
    const timeToUnicornMin = searchParams.get("min_time_to_unicorn");
    const timeToUnicornMax = searchParams.get("max_time_to_unicorn");

    return !!(
      selectedUnicornRegionId ||
      selectedCountryId ||
      sectors ||
      cities ||
      fundingStages ||
      (valuationCondition && valuationAmount) ||
      (yearFoundedCondition && yearFoundedYear) ||
      timeToUnicornMin ||
      timeToUnicornMax
    );
  }, [searchParams, selectedUnicornRegionId, selectedCountryId]);

  // Memoize current display names
  const currentUnicornRegionName = useMemo(
    () => getUnicornRegionName(selectedUnicornRegionId),
    [selectedUnicornRegionId, getUnicornRegionName],
  );
  const currentCountryName = useMemo(
    () => getCountryName(selectedCountryId),
    [selectedCountryId, getCountryName],
  );

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchCountryQuery) return countries;
    return countries.filter((country) => {
      const name = country.country_name || country.name || "";
      return name.toLowerCase().includes(searchCountryQuery.toLowerCase());
    });
  }, [countries, searchCountryQuery]);

  return (
    <div className='fixed left-0 top-0 z-10 flex items-center justify-between h-16 w-full px-4 border-b bg-[#161617] border-white/5'>
      <Button
        asChild
        className='flex !text-xs text-white bg-white/5 h-auto py-2.5 hover:bg-white/10'
      >
        <Link href='/'>
          <ArrowLeft weight='bold' className='text-base sm:mr-2.5' />
          <span className='hidden sm:block'>Go to Main Website</span>
        </Link>
      </Button>

      <Link href={"/"} className='relative h-8 sm:h-10 aspect-[468/82]'>
        <Image
          src={UnicornLogo.src}
          alt='Lucidity Insights'
          layout='fill'
          objectFit='cover'
        />
      </Link>

      <div className='flex items-center gap-2'>
        {/* Unicorn Region Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger className='items-center hidden gap-3 px-3 py-2 pr-3 text-xs rounded-md sm:inline-flex sm:px-4 bg-white/10'>
            {currentUnicornRegionName}
            {/* Select Region */}
            <CaretDown weight='bold' className='text-base' />
          </DropdownMenuTrigger>
          <DropdownMenuContent className='border border-white/5 bg-white/5 backdrop-blur-md'>
            {loading ? (
              <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
            ) : regions.length > 0 ? (
              regions.map((region) => (
                <DropdownMenuItem
                  key={region.id}
                  onClick={() =>
                    updateQuery("unicorn_regions", region.id.toString())
                  }
                  className='data-[highlighted]:bg-white/10'
                >
                  {region.name}
                </DropdownMenuItem>
              ))
            ) : (
              <DropdownMenuItem disabled>
                No unicorn regions available
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Country Dropdown */}
        <div className='relative hidden sm:inline-block'>
          <Popover open={openCountryFilter} onOpenChange={setOpenCountryFilter}>
            <PopoverTrigger asChild>
              <button className='inline-flex items-center gap-3 px-4 py-2 pr-3 text-xs rounded-md bg-white/10'>
                <span className='truncate max-w-32'>{currentCountryName}</span>
                <CaretDown weight='bold' className='text-base' />
              </button>
            </PopoverTrigger>

            {selectedCountryId && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  updateQuery("country", "");
                  setSearchCountryQuery("");
                }}
                className='absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 rounded-full p-0.5 z-20 transition-colors'
              >
                <X weight='bold' className='text-xs text-white' />
              </button>
            )}
            <PopoverContent className='w-64 p-0 border border-white/5 bg-black/95 backdrop-blur-md'>
              {loading ? (
                <div className='p-4 text-xs'>Loading...</div>
              ) : (
                <>
                  <div className='p-2 border-b border-white/5'>
                    <Input
                      type='text'
                      placeholder='Search countries...'
                      value={searchCountryQuery}
                      onChange={(e) => setSearchCountryQuery(e.target.value)}
                      className='h-8 text-xs'
                    />
                  </div>
                  <ScrollArea className='h-80'>
                    <div className='p-1'>
                      <div
                        onClick={() => {
                          updateQuery("country", "");
                          setOpenCountryFilter(false);
                          setSearchCountryQuery("");
                        }}
                        className='px-3 py-2 text-xs text-orange-400 cursor-pointer hover:bg-white/10'
                      >
                        All Countries
                      </div>
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <div
                            key={country.country_id ?? country.id}
                            onClick={() => {
                              const id = getCountryId(country);
                              updateQuery("country", id || "");
                              setOpenCountryFilter(false);
                              setSearchCountryQuery("");
                            }}
                            className='px-3 py-2 text-xs cursor-pointer hover:bg-white/10'
                          >
                            {country.country_name ||
                              country.name ||
                              "Unknown Country"}
                          </div>
                        ))
                      ) : (
                        <div className='px-3 py-2 text-xs opacity-50'>
                          {searchCountryQuery
                            ? "No countries found"
                            : "No countries available"}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Advanced Filter */}
        <Button
          onClick={() => setOpenFilters(true)}
          className='relative inline-flex items-center h-auto px-4 py-2 pr-3 text-xs bg-white rounded-md sm:gap-3 text-background'
        >
          <span className='hidden sm:block'>Advanced Filter </span>
          <Faders weight='bold' className='text-base' />
          {hasActiveAdvancedFilters && (
            <span className='absolute bg-red-600 rounded-full -top-1 -right-1 size-2.5 ring-[3px] ring-[#161617]'></span>
          )}
        </Button>
        <UnicornFilters
          open={openFilters}
          onOpenChange={setOpenFilters}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
    </div>
  );
});

export default UnicornTopbar;
